import { isItemActive } from '@server/documentation';
import type {
  SalesforceItem,
  SalesforceItemField,
  SalesforceItemRollup,
  SalesforceItemSweepAlert,
  SalesforceItemSweepAssignment,
  SalesforceItemSweepAutomation,
  SalesforceItemSweepDedupe,
  SalesforceItemSweepMatching,
  SalesforceItemSweepScheduledAssignment,
} from '../parser/ParserTypes';
import {
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_AUTOMATION,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
  SWEEP_SCHEDULED_ASSIGNMENT,
  ROLLUP,
} from '../parser/ParserTypes';
import { ConfigurationToName, UnclassifiedTypes } from './salesforce-documentation-card/types';
import { ANY_STEP_NAME, getDisplayLineTexts } from '../Automations/helper';
import { getDataFromSFFieldName } from '../pages/rollups/rollupHelpers';
import { colors } from '@sweep-io/sweep-design';
import type {
  DocumentationUserInputMetadataElement,
  TagDto,
} from '../../types/documentationUserInputs';
import { telemetry } from '../../telemetry';
import type { UserInfo } from '../../types/UserInfoTypes';
import pluralize from 'pluralize';
import { getObjectTypeColor } from '../sweep-canvas/helpers/getObjectTypeColor';
import { FunnelFieldLabels } from '../../constants/deprecated_fieldsManagementConsts';
import { formatCamelCase } from './universal-search/utils';
import { uniquePrimitives } from '../../lib/uniqueByKey';
import { createRecordTypeIdentifier } from '@server/pills';
import { FIELD } from '@server/parser';
import type { SweepFieldSummary } from '../../types/fields';
import { createConfigurationItemFromParserResponseItem } from '../parser/parserUtils';
import { AutomationType } from '../../types/enums/AutomationType';
import type { RollupDto } from '../pages/rollups/rollupTypes';
import { AutomationTypes } from '../../types/enums/AutomationTypes';

export const getObjectApiNamesFromFunnelMap = (
  funnelsData?: FunnelsData,
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  Object.values(funnelsData ?? {}).forEach((funnel) => {
    const { crmOrgId } = funnel;
    const objectName = funnel.recordType?.objectName;
    if (!crmOrgId || !objectName) return;
    if (!result[crmOrgId]) result[crmOrgId] = [];
    if (!result[crmOrgId].includes(objectName)) result[crmOrgId].push(objectName);
  });

  return result;
};

export const displayCorrectDateFormat = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW = '250px';
export const OCCUPIED_SPACE_MAIN_VIEW = '200px';
export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW_CANVAS = '300px';

export const mapMissingInternalIdToMetadataId = (item: any) => ({
  ...item,
  id: item.id === 'MissingInternalId' || !item.id ? item.metadataId : item.id,
});

export const checkIfActive = (onlyActive: boolean, item: any) =>
  !onlyActive || (onlyActive && isItemActive(item));

export const translateTypeToHumanName = (type: string) => {
  if (!type) {
    return type;
  }
  return (
    ConfigurationToName[type] ?? FunnelFieldLabels[type as SweepFieldTypes] ?? formatCamelCase(type)
  );
};

export const automationTypeToConfigurationType = (type: AutomationType) => {
  switch (type) {
    case AutomationType.Alert:
      return SWEEP_ALERT;
    case AutomationType.Assignment:
      return SWEEP_ASSIGNMENT;
    case AutomationType.Dedupe:
      return SWEEP_DEDUPE;
    case AutomationType.Matching:
      return SWEEP_MATCHING;
    case AutomationType.ScheduledAssignment:
      return SWEEP_SCHEDULED_ASSIGNMENT;
    case AutomationType.Default:
      return SWEEP_AUTOMATION;

    case AutomationType.ScheduledReport:
    case AutomationType.ScheduledJob:
    case AutomationType.MarketingAttributionModel:
      //no documentation support yet
      break;

    default:
      telemetry.captureError(new Error('unknown type in automationTypeToConfigurationType'), {
        type,
      });
      break;
  }
};

export const userInputMetadataElementToSalesforceItem = (
  el: DocumentationUserInputMetadataElement,
): SalesforceItem => {
  if (el.source === 'salesforce') {
    const cp = el.element;
    return createConfigurationItemFromParserResponseItem({
      responseItem: cp,
      keyType: cp.type,
      objectApiName: cp.objectName ?? '',
    });
  }
  if (el.source === 'automation') {
    return createConfigurationItemFromAutomation(el.sweepAutomation) as SalesforceItem;
  }
  return createConfigurationItemFromRollupField(el.sweepRollup);
};

export const createConfigurationItemFromTag = (tag: TagDto): SalesforceItem => ({
  id: tag.id,
  name: tag.name,
  label: tag.name,

  objectApiNames: [],
  type: UnclassifiedTypes.tag,
  isActive: true,
  parentType: UnclassifiedTypes.tag,
  metadataId: '',
});

const buildFieldMetadataId = (objectName: string, sfFieldName: string) =>
  `salesforce.${objectName}.field.${sfFieldName}`;

export const createConfigurationItemFromSweepField = (
  sweepField: SweepFieldSummary,
): SalesforceItemField => ({
  id: sweepField.id ?? '',
  name: getDataFromSFFieldName(sweepField?.sfFieldName)?.[1] ?? '',
  label: sweepField.sweepFieldName ?? '',

  objectApiNames: [sweepField.objectName],
  type: sweepField.fieldType,
  fieldType: sweepField.fieldType,
  isActive: true,
  parentType: FIELD,
  metadataId: buildFieldMetadataId(sweepField.objectName, sweepField.sfField?.name ?? ''),
});

export const createConfigurationItemFromRollupField = (
  rollup: RollupDto,
  { usage, link }: { usage?: number; link?: string } = {},
): SalesforceItemRollup => ({
  id: rollup.rollupId,
  name: getDataFromSFFieldName(rollup.rollupField?.sfFieldName)?.[1] ?? '',
  label: rollup.rollupField.sweepFieldName ?? '',
  objectApiNames: [rollup.objectName],
  type: rollup.rollupField.fieldType,
  description: rollup.rollupField.description,
  createdAt: displayCorrectDateFormat(rollup.createdAt),
  createdBy: rollup.createdById ?? '',
  updatedAt: displayCorrectDateFormat(rollup.updatedAt),
  updatedBy: rollup.updatedById ?? '',
  helpText: rollup.rollupField.helpText,
  isMandatory: rollup.rollupField.isRequired,
  formula: (rollup.rollupField.properties as { formula?: string } | undefined)?.formula,
  usage,
  link,
  isActive: rollup.isActive,
  isRollup: true,
  parentType: ROLLUP,
  metadataId: rollup.rollupId,
  fieldType: rollup.rollupField.fieldType,
});

export const getRecordTypeNamesFromAutomation = (automation?: AutomationStructureNew) => {
  if (!automation) {
    return [];
  }

  const funnelRecordTypeName = automation.funnelRecordType?.name
    ? createRecordTypeIdentifier({
        recordTypeName: automation.funnelRecordType?.name ?? '',
        funnelId: automation.automationDetails?.funnelId ?? '',
        objectName: automation.objectName ?? '',
      })
    : '';

  const rtNames =
    automation.automationDetails?.recordTypes?.map((rt) =>
      createRecordTypeIdentifier({
        recordTypeName: rt.name,
        funnelId: '',
        objectName: automation.objectName ?? '',
      }),
    ) ?? [];

  return uniquePrimitives([funnelRecordTypeName, ...rtNames].filter((rtName) => rtName !== ''));
};

export const getDocumentationStepsFromAutomation = (
  automation: AutomationStructureNew,
  propToUse: '_stageId' | 'stageName',
): string[] => {
  const { automationDetails } = automation;
  if (automationDetails.triggerType === AutomationTypes.FieldUpdate) {
    return [];
  }

  const steps = [
    automationDetails.automationParams.stageEnter?.toStage?.[propToUse],
    automationDetails.automationParams.stageExit?.toStage?.[propToUse],
    automationDetails.automationParams.stageExit?.fromStage?.[propToUse],
    automationDetails.automationParams.timeInStep?.stage?.[propToUse],
  ].filter((step) => !!step);

  const filteredSteps = steps.filter((nameOrId) =>
    !!nameOrId ? nameOrId !== ANY_STEP_NAME : true,
  );
  return filteredSteps as string[];
};

export const createConfigurationItemFromAutomation = (
  automation?: AutomationStructureNew,
  sweepUsers?: UserInfo[],
):
  | SalesforceItemSweepAutomation
  | SalesforceItemSweepAlert
  | SalesforceItemSweepAssignment
  | SalesforceItemSweepScheduledAssignment
  | SalesforceItemSweepDedupe
  | SalesforceItemSweepMatching
  | undefined => {
  const stageIds = automation ? getDocumentationStepsFromAutomation(automation, '_stageId') : [];
  const recordTypeNames = getRecordTypeNamesFromAutomation(automation);

  const updatedBy = sweepUsers?.find((user) => user.id === automation?.updatedById);
  const createdBy = sweepUsers?.find((user) => user.id === automation?.createdById);

  const conditionsCounter =
    (automation?.automationDetails.when?.criteria as { criteria?: unknown[] })?.criteria?.length ??
    0;
  const conditionsStr = conditionsCounter
    ? `\n(${conditionsCounter} ${pluralize('Condition')})`
    : '';

  const { actionText, triggerText } = automation
    ? //temporary cast to AutomationStructureNew to avoid type errors, will fix automation types in SWEEP-9843
      getDisplayLineTexts(automation as unknown as AutomationStructureNew, true)
    : {};
  let _actionText = '';

  const components: string[] = [];

  actionText?.forEach((action) => {
    if (typeof action === 'string') {
      _actionText = _actionText === '' ? action : _actionText + ',\n' + action;
    } else {
      if (Array.isArray(action)) {
        components.push(...action.map((a) => a.toString()).flat());
      } else {
        components.push(action as string);
      }
    }
  });

  components.push(_actionText);

  const isAutomationDedupeAndMatching =
    automation?.type === AutomationType.Dedupe || automation?.type === AutomationType.Matching;

  return automation
    ? {
        id: automation.automationId,
        name: automation.name,
        label: automation.name,

        type: automation.type,
        createdAt: automation.createdAt ?? '',
        createdBy: createdBy?.name ?? 'No data',
        updatedAt: automation.updatedAt ?? '',
        updatedBy: updatedBy?.name ?? 'No data',
        objectApiNames: automation.objectName ? [automation.objectName] : [],
        stagesNames: [], //TODO
        usedByPicklistValue: stageIds,
        usedByRecordTypes: recordTypeNames,
        isActive: automation.isActive,
        trigger: triggerText + conditionsStr,
        actions: isAutomationDedupeAndMatching
          ? automation.automationDetails.actions.map(
              (action) =>
                (action.actionParams as DeduplicationActionParams | MatchingActionParams)
                  .dmType as string,
            )
          : automation.automationDetails.actions.map(
              ({ actionType }) => actionType as AutomationActionType,
            ),
        actionsToRender: components,
        metadataId: automation.automationId,
        parentType:
          automationTypeToConfigurationType(automation.type ?? AutomationType.Default) ??
          SWEEP_AUTOMATION,
      }
    : undefined;
};

export const getObjectNameColor = (objectName: string) => {
  const leadingColor = getObjectTypeColor(objectName)?.leadingColorName as keyof typeof colors;
  return (colors[leadingColor] as any)?.[100] ?? colors['blue'][100];
};

export const getConfigurationItemColor = (
  configurationItem: SalesforceItem,
  canvasColor?: string,
  tags?: TagDto[],
): string => {
  // Canvas color takes precedence (but ignore white as it's the default)
  if (canvasColor && canvasColor.toLowerCase() !== '#ffffff') {
    return canvasColor;
  }

  // For tags, use the tag's own color
  if (configurationItem.parentType === UnclassifiedTypes.tag && tags) {
    const tag = tags.find((t) => t.id === configurationItem.id);
    if (tag?.color) {
      return (
        (colors[tag.color as keyof typeof colors] as any)?.[100] ?? tag.color ?? colors.blue[100]
      );
    }
  }

  // Default: derive from object name
  const objectName = configurationItem.objectApiNames?.[0];
  return getObjectNameColor(objectName) ?? colors.blue[100];
};
